// exportPrefs — the author's last-used export format, remembered per device.
//
// Like the theme, the display name, and lastVisit, this is DEVICE state, not a
// domain fact (memory project_theme_outside_event_log): it round-trips through
// localStorage and is never emitted as an event. Only the impure shell reads or
// writes it; the resolve/parse pair below is pure. This is the smart-defaults
// "remembered export format" quick win (.claude/analysis/smart-defaults.md §7):
// the export picker preselects the format the author last packaged with, so
// authors who export to the same target every time stop re-choosing it.
//
// Seam split mirrors lastVisit.ts: `parseLastExportFormat` / `resolveInitialFormat`
// are pure (unit-tested for the degradation + fallback semantics), while
// `readLastExportFormat` / `rememberExportFormat` are the impure host-only seams.
// Storage routes through the device-store seam (deviceStore.ts) — the ONE owner
// of the `bilda.*` namespace — so this key rides the captured device track.
//
// parse, don't validate: localStorage hands back `string | null` written by ANY
// earlier build. An absent or blank value degrades to `undefined` (no remembered
// format), never a crash — the picker then falls back to the first offered format.

import type { ExportFormat } from "../components/ExportFlow/ExportFlow.js";
import { readDeviceKey, writeDeviceKey } from "./deviceStore.js";

// Namespaced key, matching the `theme.tsx` / `lastVisit.ts` convention.
const STORAGE_KEY = "bilda.lastExportFormat";

// --- Parsing (pure) ----------------------------------------------------------

// The stored value is a bare format id string. Absent (never written) or blank
// degrades to `undefined` — "nothing remembered", never a stale empty id.
export const parseLastExportFormat = (
    raw: string | null,
): string | undefined => (raw !== null && raw.length > 0 ? raw : undefined);

// --- Resolving (pure) --------------------------------------------------------

// The fallback guard, and the whole point of the feature: preselect the
// remembered format WHEN it is still one of the offered formats; otherwise the
// first offered format. A remembered id that is no longer offered (the format
// set changed, or nothing was remembered) never selects a stale/invalid format.
// An empty formats list resolves to "" — the choosing step renders its own
// empty state before this value is ever shown.
export const resolveInitialFormat = (
    remembered: string | undefined,
    formats: readonly ExportFormat[],
): string =>
    remembered !== undefined && formats.some((f) => f.id === remembered)
        ? remembered
        : (formats[0]?.id ?? "");

// --- The impure seams (shell-only, like lastVisit.ts) ------------------------

export const readLastExportFormat = (): string | undefined =>
    parseLastExportFormat(readDeviceKey(STORAGE_KEY));

export const rememberExportFormat = (formatId: string): void =>
    writeDeviceKey(STORAGE_KEY, formatId);
